/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";

import { cn } from "../../ui";
import { useDashboardContext } from "../../context/DashboardContext";
import { useAdminContext } from "../../context/admin/AdminContext";

type RegionRow = { title: string; path: string; short: string };
type StatRow = { key: string; label: string };

const REGIONS: RegionRow[] = [
  { title: "East Africa", path: "EastAfrica", short: "East" },
  { title: "Central Africa", path: "CentralAfrica", short: "Central" },
  { title: "North Africa", path: "NorthAfrica", short: "North" },
  { title: "West Africa", path: "WestAfrica", short: "West" },
  { title: "Southern Africa", path: "SouthernAfrica", short: "Southern" },
];

const LEVEL_ROWS: StatRow[] = [
  { key: "country", label: "Countries" },
  { key: "first_level", label: "First levels" },
  { key: "second_level", label: "Second levels" },
  { key: "third_level", label: "Third levels" },
  { key: "fourth_level", label: "Fourth levels" },
];

const CONTENT_ROWS: StatRow[] = [
  { key: "event", label: "Events" },
  { key: "attraction", label: "Attractions" },
  { key: "news", label: "News" },
  { key: "museum", label: "Museums" },
  { key: "restaurant", label: "Restaurants" },
  { key: "nightlife", label: "Night life" },
  { key: "hotel", label: "Hotels" },
  { key: "class", label: "Classes" },
  { key: "shopping", label: "Shopping" },
  { key: "food", label: "Food" },
  { key: "book", label: "Books" },
  { key: "neighborhood", label: "Neighborhoods" },
  { key: "art", label: "Arts" },
  { key: "movie", label: "Movies" },
  { key: "dance", label: "Dance" },
  { key: "theater", label: "Theaters" },
  { key: "best_of_africa", label: "Best of Africa" },
  { key: "cinema", label: "Cinema" },
  { key: "city_guide", label: "City guide" },
  { key: "getting_around", label: "Getting around" },
  { key: "ministry", label: "Ministries" },
  { key: "music", label: "Music" },
  { key: "party", label: "Parties" },
  { key: "safari", label: "Safaris" },
  { key: "things_to_do", label: "Things to do" },
  { key: "tips_and_advice", label: "Tips & advice" },
  { key: "tour_package", label: "Tour packages" },
  { key: "product", label: "Products" },
];

const format = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) ? v.toLocaleString() : "0";

const sumRegion = (data: Record<string, unknown> | undefined) => {
  if (!data) return 0;
  let total = 0;
  for (const r of CONTENT_ROWS) {
    const v = data[r.key];
    if (typeof v === "number" && Number.isFinite(v)) total += v;
  }
  return total;
};

const DataCell: React.FC<{ label: string; value: unknown }> = ({
  label,
  value,
}) => {
  const isZero =
    typeof value !== "number" || !Number.isFinite(value) || value === 0;
  return (
    <div className="group flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 last:border-b-0 hover:bg-gray-50">
      <span className="truncate text-[12px] text-gray-600">{label}</span>
      <span
        className={cn(
          "shrink-0 text-[12.5px] font-semibold tabular-nums",
          isZero ? "text-gray-300" : "text-gray-900",
        )}
      >
        {format(value)}
      </span>
    </div>
  );
};

const TableStats: React.FC = () => {
  const { sysAdmin } = useAdminContext();
  const { table, getTableData } = useDashboardContext();
  const [localTable, setLocalTable] = useState<Record<string, any> | null>(
    null,
  );
  const [active, setActive] = useState<string>(REGIONS[0].path);

  useEffect(() => {
    if (!table) getTableData();
    else setLocalTable(table);
  }, [table]);

  const showLevels = ["super_admin", "admin"].includes(sysAdmin?.role);
  const activeData = localTable?.[active];

  const regionTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const r of REGIONS) totals[r.path] = sumRegion(localTable?.[r.path]);
    return totals;
  }, [localTable]);

  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white"
      aria-label="Regional breakdown"
    >
      <header className="flex flex-col gap-4 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <span aria-hidden className="h-4 w-[3px] rounded-full bg-brand" />
            <h3 className="truncate text-[15px] font-semibold tracking-tight text-gray-900">
              Regional breakdown
            </h3>
          </div>
          <p className="mt-1 truncate pl-[14px] text-[11.5px] text-gray-500">
            Content grouped by macro-region. Switch regions with the tabs.
          </p>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto">
          {REGIONS.map(({ path, short, title }) => {
            const isActive = active === path;
            const total = regionTotals[path];
            return (
              <button
                key={path}
                type="button"
                onClick={() => setActive(path)}
                aria-pressed={isActive}
                className={cn(
                  "group inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-[12px] transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
                  isActive
                    ? "border-brand bg-brand text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50",
                )}
                title={title}
              >
                <span className="font-medium">{short}</span>
                <span
                  className={cn(
                    "inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-[10.5px] font-semibold tabular-nums",
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-gray-100 text-gray-600 group-hover:bg-gray-200",
                  )}
                >
                  {total.toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>
      </header>

      <div className="grid gap-6 px-5 py-5 sm:px-6 lg:grid-cols-[minmax(0,1fr)_1.5fr]">
        {showLevels ? (
          <div className="rounded-2xl border border-gray-200 bg-gray-50/50">
            <div className="border-b border-gray-200 px-3 py-2">
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
                Levels
              </p>
            </div>
            <div>
              {LEVEL_ROWS.map((row) => (
                <DataCell
                  key={row.key}
                  label={row.label}
                  value={activeData?.[row.key]}
                />
              ))}
            </div>
          </div>
        ) : null}

        <div
          className={cn(
            "rounded-2xl border border-gray-200 bg-white",
            !showLevels ? "lg:col-span-2" : null,
          )}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
              Content
            </p>
            <p className="text-[10.5px] text-gray-400">
              {CONTENT_ROWS.length} items
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {CONTENT_ROWS.map((row) => (
              <DataCell
                key={row.key}
                label={row.label}
                value={activeData?.[row.key]}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TableStats;
